import { Box } from "@mui/material";
import { Fields, FieldsData, Typography } from "@platform/design-system";
import { JSX } from "react";

import type { MappedPlan } from "@platform/api";

export interface CoverageDetailsPanelProps {
  /** Plans to display with their fields */
  plans: MappedPlan[];
}

const mapCoverageFieldsToFieldsData = (
  coverageFields: MappedPlan["fields"],
): FieldsData[] =>
  coverageFields.map((field) => {
    const allowCopy =
      field.label === "Alternate Plans" ||
      field.label === "Additional Notes" ||
      field.label === "Contribution Structure";
    return {
      ...field,
      label: allowCopy ? { name: field.label, allowCopy } : field.label,
      value: field.value ?? "",
    };
  });
/**
 * CoverageDetailsPanel
 *
 * Displays coverage details in a vertical list format.
 * Supports multiple plans per coverage, showing plan name headers when needed.
 * Designed to be used in AgGrid sidebar panels or as a standalone component.
 */
export const CoverageDetailsPanel = ({
  plans,
}: CoverageDetailsPanelProps): JSX.Element => (
  <Box
    sx={{
      width: "100%",
      p: 1,
      display: "flex",
      flexDirection: "column",
      rowGap: 2,
    }}
  >
    {plans.map((plan: MappedPlan) => (
      <Box key={plan.name}>
        {plans.length > 1 ? (
          <Typography
            variant="body-medium"
            sx={{ p: 1, borderBottom: "1px solid #E0E0E0", mb: 1 }}
          >
            <strong>{plan.name}</strong>
          </Typography>
        ) : null}
        <Fields
          data={mapCoverageFieldsToFieldsData(plan.fields)}
          gridContainerProps={{
            spacing: 1,
          }}
          gridItemProps={{
            size: 12,
            sx: {
              p: 1,
              "& .MuiTypography-root:first-of-type": {
                mb: 0.5,
              },
            },
          }}
        />
      </Box>
    ))}
  </Box>
);

export default CoverageDetailsPanel;
